import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import {Box, Typography, useTheme} from '@mui/material';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  shiftStartTime: string;
  shiftEndTime: string;
  videoSegmentStartTime: string;
  videoSegmentDuration: number;
  shiftOffset: number;
  onShiftOffsetChange: (offset: number) => void;
  adjustmentRange: number;
}

const formatTime = (seconds: number) => {
  const sign = seconds < 0 ? '-' : '+';
  const abs = Math.abs(Math.round(seconds));
  return `${sign}${abs}s`;
};

const ShiftTimeAdjustmentSlider: React.FC<Props> = ({
  videoRef,
  shiftStartTime,
  shiftEndTime,
  videoSegmentStartTime,
  videoSegmentDuration,
  shiftOffset,
  onShiftOffsetChange,
  adjustmentRange,
}) => {
  const [dragging, setDragging] = useState<'start' | 'end' | 'playback' | null>(
    null,
  );
  const [currentTime, setCurrentTime] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const shiftOffsetRef = useRef(shiftOffset);
  shiftOffsetRef.current = shiftOffset;
  const {t} = useTranslation();
  const theme = useTheme();

  // Memoize DateTime parsing and derived offset calculations
  const {originalShiftStartOffsetInSegment, originalShiftEndOffsetInSegment} =
    useMemo(() => {
      const segmentStart = DateTime.fromISO(videoSegmentStartTime);
      const originalShiftStart = DateTime.fromISO(shiftStartTime);
      const originalShiftEnd = DateTime.fromISO(shiftEndTime);

      return {
        originalShiftStartOffsetInSegment: originalShiftStart.diff(
          segmentStart,
          'seconds',
        ).seconds,
        originalShiftEndOffsetInSegment: originalShiftEnd.diff(
          segmentStart,
          'seconds',
        ).seconds,
      };
    }, [videoSegmentStartTime, shiftStartTime, shiftEndTime]);

  // Current (adjusted) shift times within the video segment
  const shiftStartOffsetInSegment =
    originalShiftStartOffsetInSegment + shiftOffset;
  const shiftEndOffsetInSegment = originalShiftEndOffsetInSegment + shiftOffset;

  // Calculate the visible range: original shift ± adjustmentRange
  const {rangeStart, totalRange} = useMemo(() => {
    const start = Math.max(
      0,
      originalShiftStartOffsetInSegment - adjustmentRange,
    );
    const end = Math.min(
      videoSegmentDuration,
      originalShiftEndOffsetInSegment + adjustmentRange,
    );
    return {rangeStart: start, rangeEnd: end, totalRange: end - start};
  }, [
    originalShiftStartOffsetInSegment,
    originalShiftEndOffsetInSegment,
    adjustmentRange,
    videoSegmentDuration,
  ]);

  // Convert time positions to percentages
  const getPercentage = useCallback(
    (timeInSeconds: number) => {
      return ((timeInSeconds - rangeStart) / totalRange) * 100;
    },
    [rangeStart, totalRange],
  );

  const startPercentage = getPercentage(shiftStartOffsetInSegment);
  const endPercentage = getPercentage(shiftEndOffsetInSegment);
  const progressPercentage = Math.max(
    0,
    Math.min(100, getPercentage(currentTime)),
  );

  // Update current video time
  useEffect(() => {
    const interval = setInterval(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [videoRef]);

  // Handle mouse/touch events
  const handlePointerDown = useCallback(
    (handle: 'start' | 'end' | 'playback') => {
      setDragging(handle);
      videoRef.current?.pause();
    },
    [videoRef],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

      // Convert percentage to time in seconds
      const timeInSegment = rangeStart + (percentage / 100) * totalRange;

      if (dragging === 'playback') {
        // Seek video directly to the pointer position
        if (videoRef.current) {
          videoRef.current.currentTime = Math.max(
            shiftStartOffsetInSegment,
            Math.min(timeInSegment, shiftEndOffsetInSegment),
          );
        }
        return;
      }

      // Determine which edge is being dragged (start or end)
      const baseTime =
        dragging === 'start'
          ? shiftStartOffsetInSegment
          : shiftEndOffsetInSegment;

      // Desired shift for this edge in seconds - round to whole seconds
      let delta = Math.round(timeInSegment - baseTime);

      // Clamp delta so offset stays within [-adjustmentRange, adjustmentRange]
      const maxPositiveDelta = adjustmentRange - shiftOffset;
      const maxNegativeDelta = -adjustmentRange - shiftOffset;

      if (delta > maxPositiveDelta) {
        delta = Math.floor(maxPositiveDelta);
      } else if (delta < maxNegativeDelta) {
        delta = Math.ceil(maxNegativeDelta);
      }

      const newOffset = Math.round(shiftOffset + delta);
      onShiftOffsetChange(newOffset);

      // Seek video to the corresponding shift boundary when dragging
      if (videoRef.current) {
        if (dragging === 'start') {
          videoRef.current.currentTime =
            originalShiftStartOffsetInSegment + newOffset;
        } else if (dragging === 'end') {
          videoRef.current.currentTime =
            originalShiftEndOffsetInSegment + newOffset;
        }
      }
    },
    [
      dragging,
      rangeStart,
      totalRange,
      shiftStartOffsetInSegment,
      shiftEndOffsetInSegment,
      adjustmentRange,
      shiftOffset,
      onShiftOffsetChange,
      originalShiftStartOffsetInSegment,
      originalShiftEndOffsetInSegment,
      videoRef,
    ],
  );

  const handlePointerUp = useCallback(() => {
    if (dragging && dragging !== 'playback' && videoRef.current) {
      videoRef.current.currentTime =
        originalShiftStartOffsetInSegment + shiftOffsetRef.current;
    }
    setDragging(null);
  }, [dragging, originalShiftStartOffsetInSegment, videoRef]);

  useEffect(() => {
    if (dragging) {
      const listener = () => handlePointerUp();
      document.addEventListener('pointerup', listener);
      return () => {
        document.removeEventListener('pointerup', listener);
      };
    }
  }, [dragging, handlePointerUp]);

  return (
    <Box>
      <Box
        ref={sliderRef}
        onPointerMove={handlePointerMove}
        sx={{
          position: 'relative',
          height: 80,
          cursor: dragging ? 'grabbing' : 'default',
          userSelect: 'none',
        }}>
        {/* Background track (semi-transparent for buffer zones) */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: 0,
            right: 0,
            height: 4,
            bgcolor: `${theme.palette.primary.main}38`,
            borderRadius: 1,
            pointerEvents: 'none',
          }}
        />

        {/* Active shift range */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: `${startPercentage}%`,
            width: `${endPercentage - startPercentage}%`,
            height: 4,
            bgcolor: 'primary.main',
            borderRadius: 1,
            transition: dragging ? 'none' : 'all 0.1s ease',
            pointerEvents: 'none',
          }}
        />

        {/* Playback position caret */}
        {progressPercentage >= 0 && progressPercentage <= 100 && (
          <Box
            onPointerDown={() => handlePointerDown('playback')}
            sx={{
              position: 'absolute',
              top: 'calc(50% - 14px)',
              left: `${progressPercentage}%`,
              transform: 'translateX(-50%)',
              zIndex: 15,
              cursor: 'grab',
              '&:active': {cursor: 'grabbing'},
            }}>
            {/* Down-pointing caret */}
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '10px solid white',
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
              }}
            />
          </Box>
        )}

        {/* Start handle - positioned above the track */}
        <Box
          onPointerDown={() => handlePointerDown('start')}
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            left: `${startPercentage}%`,
            width: 20,
            height: 20,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            cursor: 'grab',
            zIndex: dragging === 'start' ? 20 : 10,
            boxShadow: '0 0 0 8px rgba(0,0,0,0.04)',
            transition: dragging ? 'none' : 'all 0.1s ease',
            border: '2px solid currentColor',
            '&:hover': {
              boxShadow: `0 0 0 8px ${theme.palette.primary.main}1F`,
            },
            '&:active': {
              cursor: 'grabbing',
            },
          }}>
          {/* Label above handle */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              mb: 0.5,
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
              color: 'primary.main',
              fontSize: '0.75rem',
              pointerEvents: 'none',
            }}>
            {t('shiftAdjustment.shiftStart')} {formatTime(shiftOffset)}
          </Typography>
        </Box>

        {/* End handle - positioned above the track */}
        <Box
          onPointerDown={() => handlePointerDown('end')}
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            left: `${endPercentage}%`,
            width: 20,
            height: 20,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            cursor: 'grab',
            zIndex: dragging === 'end' ? 20 : 10,
            boxShadow: '0 0 0 8px rgba(0,0,0,0.04)',
            transition: dragging ? 'none' : 'all 0.1s ease',
            border: '2px solid currentColor',
            '&:hover': {
              boxShadow: `0 0 0 8px ${theme.palette.primary.main}1F`,
            },
            '&:active': {
              cursor: 'grabbing',
            },
          }}>
          {/* Label above handle */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              mb: 0.5,
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
              color: 'primary.main',
              fontSize: '0.75rem',
              pointerEvents: 'none',
            }}>
            {t('shiftAdjustment.shiftEnd')} {formatTime(shiftOffset)}
          </Typography>
        </Box>

        {/* Time labels at ends */}
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: 'calc(50% + 12px)',
            left: 0,
            color: 'text.secondary',
          }}>
          -{adjustmentRange}s
        </Typography>
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: 'calc(50% + 12px)',
            right: 0,
            color: 'text.secondary',
          }}>
          +{adjustmentRange}s
        </Typography>
      </Box>

      <Box sx={{display: 'flex', gap: 4, justifyContent: 'center'}}>
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="caption" color="text.secondary">
            {t('shiftAdjustment.bufferZone')}
          </Typography>
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: `${theme.palette.primary.main}38`,
              borderRadius: 1,
              mx: 'auto',
              mt: 0.5,
            }}
          />
        </Box>
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="caption" color="text.secondary">
            {t('shiftAdjustment.activeShift')}
          </Typography>
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'primary.main',
              borderRadius: 1,
              mx: 'auto',
              mt: 0.5,
            }}
          />
        </Box>
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="caption" color="text.secondary">
            {t('shiftAdjustment.playbackPosition')}
          </Typography>
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid white',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
              mx: 'auto',
              mt: 0.5,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ShiftTimeAdjustmentSlider;
